import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { getStorageUrl } from '../../api/axios';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import {
   Download,
   X,
   Check,
   Search,
   Minus,
   Plus,
   Maximize,
   Printer,
   ExternalLink,
   Info,
   History,
   CheckCircle2,
   XCircle,
   Clock,
   Send,
   FileText,
   Loader2,
   AlertCircle
} from 'lucide-react';
import styles from './DesignReview.module.css';
import CustomDatePicker from '../../components/Common/DatePicker/CustomDatePicker';
import NotFound from '../Error/NotFound';



const DesignReview = () => {
   const navigate = useNavigate();
   const { id } = useParams();
   const [submissionData, setSubmissionData] = useState(null);
   const [loading, setLoading] = useState(true);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [reviewStatus, setReviewStatus] = useState(null); // 'approved' or 'rejected'
   const [notes, setNotes] = useState('');
   const [consultantToClientDate, setConsultantToClientDate] = useState('');
   const [clientToContractorDate, setClientToContractorDate] = useState('');
   const [isSidebarOpen, setIsSidebarOpen] = useState(false);
   const [viewMode, setViewMode] = useState('submission'); // 'submission' or 'reference'
   const [zoom, setZoom] = useState(100);

   const fetchData = useCallback(async () => {
      setLoading(true);
      try {
         const res = await api.get(`/design-submissions/${id}`);
         if (res.data.success) {
            const data = res.data.data;
            setSubmissionData(data);
            // Pre-set review status if already completed
            if (['approved', 'rejected', 'code1', 'code2', 'code3', 'code4'].includes(data.status)) {
               setReviewStatus(data.status);
            }
            if (data.consultant_to_client_date) setConsultantToClientDate(data.consultant_to_client_date);
            if (data.client_to_contractor_date) setClientToContractorDate(data.client_to_contractor_date);
         }
      } catch (err) {
         console.error('Failed to fetch design details:', err);
         toast.error('Could not load design details');
      } finally {
         setLoading(false);
      }
   }, [id]);

   useEffect(() => {
      fetchData();
   }, [fetchData]);

   const handleReviewSubmit = async () => {
      if (!reviewStatus) {
         toast.error('Please select a review code (CODE 1, 2, 3, or 4)');
         return;
      }

      setIsSubmitting(true);
      try {
         const res = await api.post(`/design-submissions/${id}/review`, {
            status: reviewStatus,
            review_notes: notes,
            consultant_rev_sub_to_client_date: consultantToClientDate instanceof Date
               ? consultantToClientDate.toISOString().split('T')[0]
               : consultantToClientDate,
            client_rev_sub_to_consultant_date: clientToContractorDate instanceof Date
               ? clientToContractorDate.toISOString().split('T')[0]
               : clientToContractorDate
         });

         if (res.data.success) {
            toast.success(`Design ${reviewStatus} successfully`);
            navigate('/projects/design-submissions');
         }
      } catch (err) {
         console.error('Review submission failed:', err);
         toast.error(err.response?.data?.message || 'Failed to submit review');
      } finally {
         setIsSubmitting(false);
      }
   };

   const getPreviewUrl = () => {
      if (viewMode === 'reference') {
         const filePath = submissionData?.letter_of_intent?.loi_document_filepath;
         return filePath ? getStorageUrl(filePath) : null;
      }

      let filePath = submissionData?.pdf_filepath;
      if (filePath && !filePath.includes('/')) {
         filePath = `design-submissions/${filePath}`;
      }
      return filePath ? getStorageUrl(filePath) : null;
   };

   if (loading) {
      return (
         <div className={styles['design-review-container']}>
            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
            {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
            <div className={styles['content-wrapper']}>
               <Header onMenuClick={() => setIsSidebarOpen(true)} />
               <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: '20px' }}>
                  <Loader2 size={48} className={styles['spinner']} color="#f59e0b" />
                  <p style={{ color: '#64748b', fontWeight: 600 }}>Loading design submission details...</p>
               </div>
            </div>
         </div>
      );
   }

   if (!submissionData) {
      return <NotFound />;
   }



   const po = submissionData?.letter_of_intent;
   const vendor = submissionData?.vendor;
   const previewUrl = getPreviewUrl();
   const isImage = (url) => url && /\.(jpg|jpeg|png|gif|webp)$/i.test(url);

   return (
      <div className={styles['design-review-container']}>
         <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
         {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

         <div className={styles['content-wrapper']}>
            <Header onMenuClick={() => setIsSidebarOpen(true)} />

            <main className={[styles['main-content'], styles['review-main-content']].join(' ')}>
               <div className={styles['review-header-container']}>
                  <Breadcrumbs customItems={[
                     { label: 'Projects', path: '/projects' },
                     { label: 'Design Submissions', path: '/projects/design-submissions' },
                     { label: `Review DS-${id}` }
                  ]} />
                  <div className={styles['review-page-title-row']}>
                     <div className={styles['title-left']}>
                        <h1>Design Review <span className={`${styles['  ']} ${styles[`status-${submissionData?.status}`]}`}>
                           {(submissionData?.status || 'PENDING').replace('_', ' ').toUpperCase()}
                        </span></h1>
                     </div>
                     <div className={styles['view-toggle']}>
                        <button
                           className={`${styles['toggle-view-btn']} ${viewMode === 'submission' ? styles['active'] : ''}`}
                           onClick={() => setViewMode('submission')}
                        >
                           Submitted Design
                        </button>
                        <button
                           className={`${styles['toggle-view-btn']} ${viewMode === 'reference' ? styles['active'] : ''}`}
                           onClick={() => setViewMode('reference')}
                        >
                           LOI Reference
                        </button>
                     </div>
                  </div>
               </div>

               <div className={styles['review-workflow-grid']}>
                  {/* Main Viewer Area */}
                  <div className={[styles['viewer-section'], styles['card']].join(' ')}>
                     <div className={styles['viewer-toolbar']}>
                        <div className={styles['toolbar-left']}>
                           <div className={styles['file-type-info']}>
                              {viewMode === 'submission' ? <FileText size={18} /> : <Info size={18} />}
                              <span>{viewMode === 'submission' ? 'NEW SUBMISSION' : 'STORED LOI DOCUMENT'}</span>
                           </div>
                        </div>
                        <div className={styles['toolbar-center']}>
                           <button className={styles['toolbar-btn']} onClick={() => setZoom(Math.max(25, zoom - 10))}><Minus size={18} /></button>
                           <span className={styles['zoom-level']}>{zoom}%</span>
                           <button className={styles['toolbar-btn']} onClick={() => setZoom(Math.min(200, zoom + 10))}><Plus size={18} /></button>
                        </div>
                        <div className={styles['toolbar-right']}>
                           {previewUrl && (
                              <a href={previewUrl} target="_blank" rel="noopener noreferrer" className={styles['toolbar-btn']}>
                                 <ExternalLink size={18} />
                              </a>
                           )}
                        </div>
                     </div>

                     <div className={styles['drawing-preview-area']}>
                        {previewUrl ? (
                           isImage(previewUrl) ? (
                              <div className={styles['image-viewer-frame']}>
                                 <img
                                    src={previewUrl}
                                    alt="Drawing Preview"
                                    style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'center center' }}
                                 />
                              </div>
                           ) : (
                              <iframe src={previewUrl} title="PDF Viewer" className={styles['pdf-iframe']} />
                           )
                        ) : (
                           <div className={styles['no-preview-state']}>
                              <AlertCircle size={48} />
                              <p>No preview available for this document.</p>
                           </div>
                        )}
                     </div>
                  </div>

                  {/* Right Sidebar - Review Controls */}
                  <aside className={styles['review-sidebar']}>
                     <div className={[styles['sidebar-card'], styles['card']].join(' ')}>
                        <div className={styles['sidebar-header-review']}>
                           <Info size={18} className={styles['icon-blue']} />
                           <h3>Details</h3>
                        </div>
                        <div className={styles['details-list']}>
                           <div className={styles['detail-item']}>
                              <label>LOI NUMBER</label>
                              <p className={styles['po-number-link']}>{po?.loi_number || '---'}</p>
                           </div>
                           <div className={styles['detail-item']}>
                              <label>VENDOR</label>
                              <p>{vendor?.vendor_name || '---'}</p>
                           </div>
                           <div className={styles['drawing-version-row']}>
                              <div className={styles['detail-item']}>
                                 <label>REVISION</label>
                                 <p>{submissionData?.revision_code || '---'}</p>
                              </div>
                              <div className={styles['detail-item']}>
                                 <label>VERSION</label>
                                 <p>v{submissionData?.version_number || '1.0'}</p>
                              </div>
                           </div>
                        </div>
                     </div>

                     <div className={[styles['sidebar-card'], styles['card']].join(' ')}>
                        <div className={styles['sidebar-header-review']}>
                           <CheckCircle2 size={18} className={styles['icon-orange']} />
                           <h3>Decision</h3>
                        </div>
                        <div className={styles['decision-form']}>
                           <label className={styles['input-label']}>Action</label>
                           <div className={styles['decision-grid']}>
                              <button
                                 className={`${styles['review-btn']} ${styles['btn-code1']} ${reviewStatus === 'code1' ? styles['active'] : ''}`}
                                 onClick={() => setReviewStatus('code1')}
                              >
                                 <CheckCircle2 size={18} /> CODE 1
                              </button>
                              <button
                                 className={`${styles['review-btn']} ${styles['btn-code2']} ${reviewStatus === 'code2' ? styles['active'] : ''}`}
                                 onClick={() => setReviewStatus('code2')}
                              >
                                 <Info size={18} /> CODE 2
                              </button>
                              <button
                                 className={`${styles['review-btn']} ${styles['btn-code3']} ${reviewStatus === 'code3' ? styles['active'] : ''}`}
                                 onClick={() => setReviewStatus('code3')}
                              >
                                 <AlertCircle size={18} /> CODE 3
                              </button>
                              <button
                                 className={`${styles['review-btn']} ${styles['btn-code4']} ${reviewStatus === 'code4' ? styles['active'] : ''}`}
                                 onClick={() => setReviewStatus('code4')}
                              >
                                 <XCircle size={18} /> CODE 4
                              </button>
                           </div>


                           <div className={styles['form-group-review']}>
                              <label className={styles['input-label']}>Consultant Review submission to client date</label>
                              <CustomDatePicker
                                 selected={consultantToClientDate}
                                 onChange={(date) => setConsultantToClientDate(date)}
                                 placeholderText="Select date..."
                              />
                           </div>

                           <div className={styles['form-group-review']}>
                              <label className={styles['input-label']}>Client Review submission to contractor date <span className={styles['required-star']}>*</span> </label>
                              <CustomDatePicker
                                 selected={clientToContractorDate}
                                 onChange={(date) => setClientToContractorDate(date)}
                                 placeholderText="Select date..."
                              />
                           </div>

                           <div className={styles['form-group-review']}>
                              <label className={styles['input-label']}>Review Comments</label>
                              <textarea
                                 placeholder="Provide technical feedback or reason for rejection..."
                                 className={styles['review-textarea']}
                                 value={notes}
                                 onChange={(e) => setNotes(e.target.value)}
                              ></textarea>
                           </div>
                           <button
                              className={styles['btn-submit-review']}
                              onClick={handleReviewSubmit}
                              disabled={isSubmitting}
                           >
                              {isSubmitting ? <Loader2 className={styles['spinner']} /> : <Send size={18} />}
                              <span>Submit Review</span>
                           </button>
                        </div>
                     </div>
                  </aside>
               </div>
            </main>
         </div>
      </div>
   );
};

export default DesignReview;
